import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { adminApi } from '@/api';
import { Button, Icon, Input, Modal, useToast } from '@/components/ui';
import { errorMessage } from '@/hooks/useApiError';

/**
 * The identity decision on one student, wherever the student appears.
 *
 * Deliberately the same pair of buttons on the review list and on the student
 * record, because they are the same decision taken with more or less of the
 * evidence in front of you -- the record has the whole profile, the list has
 * the three fields that usually settle it.
 *
 * `student` is `{ id, full_name, identity_verification_status }`, where the id
 * is the *profile* id, which is what the endpoint takes.
 *
 * `compact` gives the short card-footer labels ("Verify", "Refuse") in the
 * tinted small buttons every list uses.
 */
export function StudentIdentityActions({ student, size = 'sm', compact = false, className }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [refusing, setRefusing] = useState(false);
  const [reason, setReason] = useState('');

  function invalidate() {
    // The badge appears on the review list, the student record and the
    // directory row, so refresh the console rather than guess the caller.
    void queryClient.invalidateQueries({
      predicate: (query) => String(query.queryKey[0]).startsWith('admin-'),
    });
  }

  const approve = useMutation({
    mutationFn: () => adminApi.approveStudent(student.id),
    onSuccess: (updated) => {
      toast.success(`${updated.full_name} is verified`);
      invalidate();
    },
    onError: (error) => toast.error(errorMessage(error, 'Could not verify the student')),
  });

  const refuse = useMutation({
    mutationFn: (text) => adminApi.rejectStudent(student.id, text),
    onSuccess: () => {
      toast.success('Verification refused');
      invalidate();
      setRefusing(false);
      setReason('');
    },
    onError: (error) => toast.error(errorMessage(error, 'Could not refuse the check')),
  });

  const decided = student.identity_verification_status;

  function openRefusal() {
    setReason('');
    setRefusing(true);
  }

  return (
    <div className={className ?? 'flex flex-wrap items-center gap-2'}>
      {decided !== 'REJECTED' &&
        (compact ? (
          <Button
            size="sm"
            variant="tintDanger"
            onClick={openRefusal}
            title={`Refuse ${student.full_name}'s identity check, with a reason`}
          >
            <Icon name="close" size={14} />
            Refuse
          </Button>
        ) : (
          <Button
            variant="tintDanger"
            size={size}
            onClick={openRefusal}
            title={`Refuse ${student.full_name}'s identity check, with a reason`}
          >
            <Icon name="close" size={15} />
            Refuse identity
          </Button>
        ))}

      {decided !== 'VERIFIED' &&
        (compact ? (
          <Button
            size="sm"
            variant="tintPositive"
            isLoading={approve.isPending}
            onClick={() => approve.mutate()}
            title={`Confirm ${student.full_name} is who they say they are`}
          >
            <Icon name="check" size={14} />
            Verify
          </Button>
        ) : (
          <Button
            size={size}
            variant="success"
            isLoading={approve.isPending}
            onClick={() => approve.mutate()}
            title={`Confirm ${student.full_name} is who they say they are`}
          >
            <Icon name="shieldCheck" size={15} />
            Verify identity
          </Button>
        ))}
      <Modal
        open={refusing}
        title={`Refuse ${student.full_name}`}
        description="The student is told the reason, and it is recorded in the audit log."
        onClose={() => setRefusing(false)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setRefusing(false)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              // The backend asks for four characters; a one-word refusal is
              // not something the student can do anything with anyway.
              disabled={reason.trim().length < 4}
              isLoading={refuse.isPending}
              onClick={() => refuse.mutate(reason.trim())}
            >
              Refuse
            </Button>
          </>
        }
      >
        <Input
          label="Reason"
          required
          placeholder="The university address does not match the name given"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
      </Modal>
    </div>
  );
}
